import React from 'react';
import PropTypes from 'prop-types';
import CustomPropTypes from 'CustomPropTypes';
import Waypoint from 'react-waypoint';
import SelfServeBuilderCanvasOverlay from 'SelfServeBuilder/SelfServeBuilderCanvasOverlay';
import { bindActionCreators } from 'redux';
import * as ReactRedux from 'react-redux';
import * as SectionOrganizerActions from 'SelfServePostBuilderSidePanel/SectionOrganizer/SectionOrganizerActions';
import SelfServeBuilderPageCanvas from 'SelfServeBuilder/SelfServeBuilderPageCanvas';
import UploadButton from 'UploadButton';
import * as SelfServeBuilderActions from './SelfServeBuilderActions';

export class SelfServeBuilderPDFPageCanvas extends React.Component {
  constructor(props) {
    super(props);

    this.onEnter = this.onEnter.bind(this);
    this.onLeave = this.onLeave.bind(this);
    this.addPaddingTop = this.addPaddingTop.bind(this);

    this.state = {
      paddingTop: 0,
    };
  }

  shouldComponentUpdate(nextProps, nextState) {
    const {
      popupsOnPage: currentPopupsOnPage,
      popupGroupsOnPage: currentGroupsOnPage,
      activePopup: currentActivePopup,
      height: currentHeight,
      width: currentWidth,
    } = this.props;
    const {
      popupsOnPage: nextPopupsOnPage,
      popupGroupsOnPage: nextGroupsOnPage,
      activePopup: nextActivePopup,
      height: nextHeight,
      width: nextWidth,
    } = nextProps;

    const currentGroupsOnPageAsString = JSON.stringify(currentGroupsOnPage);
    const nextGroupsOnPageAsString = JSON.stringify(nextGroupsOnPage);

    const popupsDiffer = JSON.stringify(currentPopupsOnPage) !== JSON.stringify(nextPopupsOnPage);
    const popupGroupsDiffer = currentGroupsOnPageAsString !== nextGroupsOnPageAsString;
    const currentPageActive = currentActivePopup in currentPopupsOnPage;
    const nextPageActive = nextActivePopup in nextPopupsOnPage;
    const stateChanged = JSON.stringify(this.state) !== JSON.stringify(nextState);
    const dimensionsChanged = currentHeight !== nextHeight || currentWidth !== nextWidth;
    const pageChanged = currentPageActive || nextPageActive;
    const popupsChanged = popupsDiffer || popupGroupsDiffer;
    return popupsChanged || pageChanged || stateChanged || dimensionsChanged;
  }

  componentDidUpdate(prevProps) {
    const paginationChange = this.props.currentPagePagination !== prevProps.currentPagePagination;
    const scrollToPage = parseInt(
      this.props.currentPagePagination,
      10,
    ) === (this.props.page.pageIndex + 1);
    if (paginationChange && scrollToPage) {
      this.pdfPageCanvas.scrollIntoView();
    }
  }

  onEnter() {
    if (!this.props.waypointsEnabled) {
      return;
    }
    this.props.setCurrentPage(this.props.page.pageIndex);
  }

  onLeave({ currentPosition }) {
    if (!this.props.waypointsEnabled) {
      return;
    }
    if (currentPosition === Waypoint.above) {
      this.props.setCurrentPage(this.props.page.pageIndex + 1);
      return;
    }
    if (currentPosition === Waypoint.below) {
      this.props.setCurrentPage(this.props.page.pageIndex);
    }
  }

  addPaddingTop(paddingTop) {
    if (isNaN(paddingTop)) {
      this.setState({ paddingTop: 0 });
    } else {
      this.setState({ paddingTop });
    }
  }

  render() {
    // when scrolling through pages, we want to update the page number when
    // a portion of the canvas is visible
    const WAYPOINT_OFFSET_RATIO = 0.18;

    const { page, width, height, scale, isPreviewMode } = this.props;
    return (
      <div
        id={`selfServeBuilderPDFPageCanvas${page.pageIndex}`}
      >
        <div
          className="self-serve-builder-pdf-page"
          ref={(el) => { this.pdfPageCanvas = el; }}
        >
          <div
            className="page self-serve-builder-canvas-wrapper"
            data-page-number={page.pageIndex + 1}
            style={{
              height,
              width,
              paddingTop: this.state.paddingTop,
            }}

          >
            <SelfServeBuilderCanvasOverlay
              addPaddingTop={this.addPaddingTop}
              getSuperScriptValue={this.props.getSuperScriptValue}
              height={height}
              pageIndex={page.pageIndex}
              popupGroupsOnPage={this.props.popupGroupsOnPage}
              popupsOnPage={this.props.popupsOnPage}
              isPreviewMode={isPreviewMode}
              width={width}
              zoomFactor={this.props.zoomFactor}
            />
            <SelfServeBuilderPageCanvas
              height={height}
              page={page}
              width={width}
              scale={scale}
            />
            {
              this.props.isSampleState && !isPreviewMode && (
                <UploadButton
                  id="startBuildingTemplateCTA"
                  onClick={() => {
                    window._track('Upload Template | Sample Doc', 'Template Builder');
                  }}
                  style={{
                    margin: '12px 0px',
                  }}
                  text="Start Building Your Own Template"
                />
              )
            }
          </div>
        </div>
        {!isPreviewMode &&
          <Waypoint
            bottomOffset={`${parseInt(height * WAYPOINT_OFFSET_RATIO, 10)}px`}
            onEnter={this.onEnter}
            onLeave={this.onLeave}
            topOffset={`${parseInt(height * WAYPOINT_OFFSET_RATIO, 10)}px`}
          />
        }
      </div>
    );
  }
}

SelfServeBuilderPDFPageCanvas.defaultProps = {
  activePopup: '',
  activePopupType: '',
  isPreviewMode: false,
  isSampleState: false,
  zoomFactor: 1,
};

SelfServeBuilderPDFPageCanvas.propTypes = {
  activePopup: PropTypes.string,
  currentPagePagination: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]).isRequired,
  getSuperScriptValue: PropTypes.func.isRequired,
  height: PropTypes.number.isRequired,
  isPreviewMode: PropTypes.bool.isRequired,
  isSampleState: PropTypes.bool,
  page: PropTypes.shape().isRequired,
  popupGroupsOnPage: CustomPropTypes.hashmapOf(
    PropTypes.shape({
      label: PropTypes.string,
      type: PropTypes.string,
      id: PropTypes.string,
      pageIdx: PropTypes.number,
    }),
  ).isRequired,
  popupsOnPage: CustomPropTypes.hashmapOf(
    PropTypes.shape({
      label: PropTypes.string,
      type: PropTypes.string,
      id: PropTypes.string,
      pageIdx: PropTypes.number,
    }),
  ).isRequired,
  scale: PropTypes.number.isRequired,
  setCurrentPage: PropTypes.func.isRequired,
  waypointsEnabled: PropTypes.bool.isRequired,
  width: PropTypes.number.isRequired,
  zoomFactor: PropTypes.number,
};

const mapStateToProps = ({
  SelfServeBuilderReducer,
  popupHandler,
}) => (
  {
    activePopup: popupHandler.activePopup,
    currentPage: SelfServeBuilderReducer.currentPage,
    currentPagePagination: SelfServeBuilderReducer.currentPagePagination,
    isSampleState: SelfServeBuilderReducer.isSampleState,
    waypointsEnabled: SelfServeBuilderReducer.waypointsEnabled,
  }
);

const mapDispatchToProps = dispatch => (
  bindActionCreators({
    addSectionOrganizerSection: SectionOrganizerActions.addSectionOrganizerSection,
    setCurrentPage: SelfServeBuilderActions.setCurrentPage,
  }, dispatch)
);

const SelfServeBuilderPDFPageCanvasWithRedux = ReactRedux.connect(
  mapStateToProps,
  mapDispatchToProps,
)(SelfServeBuilderPDFPageCanvas);

export default SelfServeBuilderPDFPageCanvasWithRedux;



// WEBPACK FOOTER //
// ./src-react/SelfServeBuilder/SelfServeBuilderPDFPageCanvas.jsx